import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppLayout } from "./layouts/AppLayout";
import { DashboardLayout } from "./layouts/dashboard/DashboardLayout";
import { BillingView } from "./views/admin/billing/BillingView";
import { PaymentMethodCreateView } from "./views/admin/billing/PaymentMethodCreateView";
import { PaymentMethodEditView } from "./views/admin/billing/PaymentMethodEditView";
import { PaymentView } from "./views/admin/billing/PaymentView";
import { ConfigurationView } from "./views/admin/configuration/ConfigurationView";
import { CustomerCreateView } from "./views/admin/customers/CustomerCreateView";
import { CustomerEditView } from "./views/admin/customers/CustomerEditView";
import { CustomersView } from "./views/admin/customers/CustomersView";
import { CustomerView } from "./views/admin/customers/CustomerView";
import { DashboardUserCreateView } from "./views/admin/dashboard-users/DashboardUserCreateView";
import { DashboardUserEditView } from "./views/admin/dashboard-users/DashboardUserEditView";
import { DashboardUsersView } from "./views/admin/dashboard-users/DashboardUsersView";
import { DashboardUserView } from "./views/admin/dashboard-users/DashboardUserView";
import { OpenApiProductsView } from "./views/admin/open-api-products/OpenApiProductsView";
import { OpenApiProductView } from "./views/admin/open-api-products/OpenApiProductView";
import { ApiCredentialCreateView } from "./views/api-credentials/ApiCredentialCreateView";
import { ApiCredentialsView } from "./views/api-credentials/ApiCredentialsView";
import { ApiExamplesView } from "./views/ApiExamplesView";
import { CustomerProductsView } from "./views/customer-products/CustomerProductsView";
import { Error404View } from "./views/error/Error404View";
import { IndexView } from "./views/index/IndexView";
import { OrderItemView } from "./views/orders/OrderItemView";
import { OrdersView } from "./views/orders/OrdersView";
import { OrdersViewV2 } from "./views/orders/OrdersViewV2";
import { OrderView } from "./views/orders/OrderView";
import { OrderViewV2 } from "./views/orders/OrderViewV2";
import { OrderDetailsTab } from "./views/orders/tabs/OrderDetailsTab";
import { OrderItemsTab } from "./views/orders/tabs/OrderItemsTab";
import { OrderEventLogsTab } from "./views/orders/tabs/OrderEventLogsTab";
import { OrderShipmentsTab } from "./views/orders/tabs/OrderShipmentsTab";
import { PrintAssetsView } from "./views/print-asset/PrintAssetsView";
import { PrintAssetView } from "./views/print-asset/PrintAssetView";
import { SettingsEditAccountPasswordView } from "./views/settings/SettingsEditAccountPasswordView";
import { SettingsEditAccountView } from "./views/settings/SettingsEditAccountView";
import { SettingsView } from "./views/settings/SettingsView";
import { DesignLayout } from "./views/temporary/DesignLayout";
import { DesignV2View } from "./views/temporary/DesignV2View";
import { DesignView } from "./views/temporary/DesignView";
import { GraphQL } from "@rpi/openapi-api";
import { UploadsView } from "./views/admin/uploads/UploadsView";
import { UploadFilesView } from "./views/admin/uploads/UploadFilesView";
import { UploadsCollectionView } from "./views/admin/uploads/UploadsCollectionView";
import { TaxFileUploadView } from "./views/admin/billing/TaxFileUploadView";
import { AuthContainer } from "./components/AuthContainer";
import { AuthenticatorView } from "./views/auth/AuthenticatorView";
import { MantineProvider } from "@mantine/core";
import { rpaTheme } from "./theme/rpaTheme";
import { SetupPayment } from "./views/onBoarding/SetupPayment";
import { OnBoarding } from "./views/onBoarding/OnBoarding";
import { CustomerEditTypeView } from "./views/admin/customers/CustomerEditTypeView";
import { SandboxGuard } from "./components/SandboxGuard";
import { withHandle } from "./utils/route.util";
import {
  productCreateRoute,
  productDetailsRoute,
  productEditRoute,
  productFiltersRoute,
} from "./views/customer-products/routes";
import { DashboardView } from "./views/dashboard/DashboardView";
import {
  OpenApiProductSkuEditor,
  OpenApiProductSkuNavigator,
} from "./views/admin/open-api-products/OpenApiProductSkuNavigator";
import { featureFlags } from "./featureFlags";

export const DASHBOARD_PATH = "/dashboard";
const ADMIN_PATH = "admin";
export const ADMIN_DASHBOARD_PATH = `${DASHBOARD_PATH}/${ADMIN_PATH}`;

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout.Private />,
    errorElement: <Error404View />,
    children: [
      {
        path: "onboarding",
        element: (
          <MantineProvider theme={rpaTheme} withCSSVariables withGlobalStyles>
            <AuthContainer>
              <Outlet />
            </AuthContainer>
          </MantineProvider>
        ),
        children: [
          {
            index: true,
            element: <OnBoarding />,
            ...withHandle("onboarding"),
          },
          {
            path: "setup-payment",
            element: <SetupPayment />,
            ...withHandle("setup-payment"),
          },
        ],
      },
      {
        path: "dashboard",
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <DashboardView />,
            ...withHandle("dashboard"),
          },
          {
            path: "settings",
            element: <SettingsView />,
            ...withHandle("settings"),
            children: [
              {
                path: "edit-account",
                element: <SettingsEditAccountView />,
                ...withHandle("settings-edit-account"),
              },
              {
                path: "edit-password",
                element: <SettingsEditAccountPasswordView />,
                ...withHandle("settings-edit-account"),
              },
            ],
          },
          {
            path: "customer-products",
            element: <CustomerProductsView />,
            ...withHandle("customer-products"),
            children: [
              productCreateRoute,
              productDetailsRoute,
              ...(productFiltersRoute ? [productFiltersRoute] : []),
              ...(productEditRoute ? [productEditRoute] : []),
            ],
          },
          {
            path: "print-assets",
            children: [
              {
                index: true,
                element: <PrintAssetsView />,
                ...withHandle("print-assets"),
              },
              {
                path: ":id",
                element: <PrintAssetView />,
                ...withHandle("print-asset-detail"),
              },
            ],
          },
          {
            path: "orders",
            children: [
              {
                index: true,
                element: featureFlags.newOrderManagement ? <OrdersViewV2 /> : <OrdersView />,
                ...withHandle("orders"),
              },
              {
                path: ":customerPublicId/:customerOrderId",
                element: featureFlags.newOrderManagement ? <OrderViewV2 /> : <OrderView />,
                ...withHandle("order-detail", { navigateOnSandboxToggle: "/dashboard/orders" }),
                ...(featureFlags.newOrderManagement
                  ? {
                      children: [
                        {
                          index: true,
                          element: <Navigate to="details" replace />,
                        },
                        {
                          path: "details",
                          element: <OrderDetailsTab />,
                        },
                        {
                          path: "items",
                          element: <OrderItemsTab />,
                        },
                        {
                          path: "event-logs",
                          element: <OrderEventLogsTab />,
                        },
                        {
                          path: "shipments",
                          element: <OrderShipmentsTab />,
                        },
                        {
                          path: "*",
                          element: <Navigate to="details" replace />,
                        },
                      ],
                    }
                  : {}),
              },
              {
                path: ":customerPublicId/:customerOrderId/items/:itemId",
                element: <OrderItemView />,
                ...withHandle("order-item"),
              },
            ],
          },
          {
            path: "api-credentials",
            element: <ApiCredentialsView />,
            ...withHandle("api-credentials"),
            children: [
              {
                path: "create",
                ...withHandle("api-credential-create", {
                  requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin, GraphQL.DashboardUserRole.User],
                }),
                element: <ApiCredentialCreateView />,
              },
            ],
          },
          {
            path: "api-examples",
            element: <ApiExamplesView />,
            ...withHandle("api-examples"),
          },
          {
            path: ADMIN_PATH,
            ...withHandle(ADMIN_PATH, {
              requiredRole: [GraphQL.DashboardUserRole.SuperAdmin, GraphQL.DashboardUserRole.CustomerAdmin],
            }),
            children: [
              {
                path: "customers",
                element: <CustomersView />,
                ...withHandle("admin-customers", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    path: "create",
                    element: <CustomerCreateView />,
                    ...withHandle("admin-customer-create"),
                  },
                ],
              },
              {
                path: "customers/:customerPublicId",
                element: <CustomerView />,
                ...withHandle("admin-customer-detail", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    path: "edit",
                    element: <CustomerEditView />,
                    ...withHandle("admin-customer-edit"),
                  },
                  {
                    path: "edit-type",
                    element: <CustomerEditTypeView />,
                    ...withHandle("admin-customer-edit-type"),
                  },
                ],
              },
              {
                path: "open-api-products",
                ...withHandle("admin-open-api-products", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                element: <OpenApiProductsView />,
                children: [
                  {
                    path: "create",
                    element: <OpenApiProductSkuNavigator />,
                    ...withHandle("admin-open-api-product-create"),
                  },
                  {
                    path: "edit/:id",
                    element: <OpenApiProductSkuEditor />,
                    ...withHandle("admin-open-api-product-edit"),
                  },
                  {
                    path: ":id",
                    element: <OpenApiProductView />,
                    ...withHandle("admin-open-api-product-detail"),
                  },
                ],
              },
              {
                path: "dashboard-users",
                element: <DashboardUsersView />,
                ...withHandle("admin-dashboard-users"),
                children: [
                  {
                    path: "create",
                    element: <DashboardUserCreateView />,
                    ...withHandle("admin-dashboard-user-create"),
                  },
                  {
                    path: ":id",
                    element: <DashboardUserView />,
                    ...withHandle("admin-dashboard-user-detail"),
                  },
                  {
                    path: ":id/edit",
                    element: <DashboardUserEditView />,
                    ...withHandle("admin-dashboard-user-edit"),
                  },
                ],
              },
              {
                path: "billing",
                element: <BillingView />,
                ...withHandle("admin-billing"),
                children: [
                  {
                    path: "payment-methods",
                    handle: {
                      requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin, GraphQL.DashboardUserRole.User],
                    },
                    children: [
                      {
                        path: "create",
                        element: <PaymentMethodCreateView redirect="../../" />,
                        ...withHandle("payment-method-create"),
                      },
                      {
                        path: ":token/edit",
                        element: <PaymentMethodEditView redirect="../../" />,
                        ...withHandle("payment-method-edit"),
                      },
                    ],
                  },
                  {
                    path: "file-upload",
                    ...withHandle("admin-billing-file-upload", {
                      requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin],
                    }),
                    element: <TaxFileUploadView />,
                  },
                ],
              },
              {
                path: "uploads",
                element: <UploadsView />,
                ...withHandle("admin-uploads", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    path: "upload",
                    element: <UploadFilesView />,
                    ...withHandle("admin-upload-files"),
                  },
                ],
              },
              {
                path: "uploads",
                ...withHandle("uploads", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    path: "customers/:customerPublicId/collections/:collectionId",
                    element: <UploadsCollectionView />,
                    ...withHandle("admin-uploads-collection"),
                  },
                ],
              },
              {
                path: "payments",
                element: <SandboxGuard redirectTo="/dashboard/admin/billing" />,
                children: [
                  {
                    path: ":id",
                    element: <PaymentView />,
                    ...withHandle("admin-payment-detail"),
                  },
                ],
              },
              {
                path: "configuration",
                ...withHandle("configuration", {
                  requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                }),
                children: [
                  {
                    index: true,
                    element: <ConfigurationView />,
                    ...withHandle("admin-configuration"),
                  },
                ],
              },
            ],
          },
          {
            path: "*",
            element: <Error404View root={false} hideButton />,
            ...withHandle("not-found"),
          },
        ],
      },
    ],
  },
  {
    path: "/",
    element: <AppLayout.Public />,
    errorElement: <Error404View />,
    children: [
      {
        index: true,
        element: <IndexView />,
        ...withHandle("index"),
      },
      {
        path: "auth",
        element: (
          <MantineProvider theme={rpaTheme} withCSSVariables withGlobalStyles>
            <Outlet />
          </MantineProvider>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="sign-in" replace />,
          },
          {
            path: "sign-in",
            index: true,
            element: (
              <AuthContainer hideRightPane>
                <AuthenticatorView initialState="signIn" />
              </AuthContainer>
            ),
            ...withHandle("sign-in"),
          },
          {
            path: "sign-up",
            element: (
              <AuthContainer>
                <AuthenticatorView initialState="signUp" />
              </AuthContainer>
            ),
            ...withHandle("sign-up"),
          },
          {
            path: "reset-password",
            element: (
              <AuthContainer hideRightPane>
                <AuthenticatorView initialState="resetPassword" />
              </AuthContainer>
            ),
            ...withHandle("reset-password"),
          },
        ],
      },
      {
        path: "design",
        element: <DesignLayout />,
        children: [
          {
            index: true,
            element: <DesignView />,
            ...withHandle("design"),
          },
          {
            path: "v2",
            element: <DesignV2View />,
            ...withHandle("design-v2"),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Error404View />,
    ...withHandle("not-found"),
  },
]);
